import React, { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY, NAV_LINKS } from "@/data/site";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goto = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "nav-scrolled" : "nav-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-4 flex items-center justify-between">
        {/* Logo */}
        <button
          data-testid="nav-logo"
          onClick={() => goto("#home")}
          className={`flex items-center gap-3 group ${scrolled ? "text-slate-900" : "text-white"}`}
        >
          <img
            src={COMPANY.logo}
            alt={COMPANY.name}
            className="h-12 w-12 object-contain drop-shadow-sm"
          />
          <div className="text-left leading-tight">
            <div className="font-display text-sm sm:text-base font-medium tracking-tight">{COMPANY.shortName}</div>
            <div className={`text-[10px] uppercase tracking-[0.22em] ${scrolled ? "text-slate-500" : "text-white/70"}`}>
              Glass & Aluminium Installation
            </div>
          </div>
        </button>

        {/* Desktop links */}
        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => goto(l.href)}
              className={`nav-link text-sm font-medium ${scrolled ? "text-slate-700 hover:text-slate-950" : "text-white/90 hover:text-white"}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        {/* Right CTA */}
        <div className="hidden lg:flex items-center gap-4">
          <a
            data-testid="nav-phone-link"
            href={`tel:${COMPANY.phoneRaw}`}
            className={`text-sm font-medium flex items-center gap-2 ${scrolled ? "text-slate-700" : "text-white/90"}`}
          >
            <Phone className="h-4 w-4" /> {COMPANY.phone}
          </a>
          <button
            data-testid="nav-quote-cta"
            onClick={() => goto("#contact")}
            className="bg-blue-600 hover:bg-blue-700 text-white text-xs uppercase tracking-[0.18em] font-semibold px-6 py-3 transition-colors"
          >
            Get a Quote
          </button>
        </div>

        {/* Mobile menu button */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((s) => !s)}
          className={`lg:hidden p-2 -mr-2 ${scrolled ? "text-slate-900" : "text-white"}`}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-slate-200 shadow-xl">
          <div className="px-6 py-6 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => goto(l.href)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className="text-left py-3 text-slate-800 font-medium border-b border-slate-100"
              >
                {l.label}
              </button>
            ))}
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              className="mt-3 text-sm text-slate-600 flex items-center gap-2"
            >
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
            <button
              onClick={() => goto("#contact")}
              data-testid="mobile-quote-cta"
              className="mt-4 bg-blue-600 text-white text-xs uppercase tracking-[0.18em] font-semibold px-6 py-3.5"
            >
              Get a Quote
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
